/**
 * Commandes fournisseurs, adossées à Supabase.
 *
 * La répartition du panier par fournisseur et l'incrément du stock à
 * réception vivent tous deux en base (`create_material_orders_from_basket`,
 * `receive_material_order`) : ce fichier ne fait qu'appeler ces RPC et
 * projeter `material_orders` pour la liste.
 */
import type {
  ExecutionContext,
  MaterialOrder,
  MaterialOrderLine,
  MaterialOrdersRepository,
  MaterialOrderStatus,
} from "@dker/core";
import { DomainError } from "@dker/core";
import type { DbClientFactory } from "../ports.js";

/* eslint-disable @typescript-eslint/no-explicit-any */
type Row = Record<string, any>;

const num = (v: unknown): number => Number(v ?? 0);

function toLines(items: unknown): MaterialOrderLine[] {
  if (!Array.isArray(items)) return [];
  return items.map((line: Row) => ({
    catalogItemId: line.catalog_item_id,
    name: line.name,
    unit: line.unit,
    unitCost: num(line.unit_cost),
    quantity: num(line.quantity),
  }));
}

function toOrder(row: Row): MaterialOrder {
  return {
    id: row.id,
    projectId: row.project_id ?? null,
    supplierId: row.supplier_id ?? null,
    items: toLines(row.items),
    totalHt: num(row.total_ht),
    status: row.status as MaterialOrderStatus,
    neededBy: row.needed_by ?? null,
    orderedAt: row.ordered_at ?? null,
    expectedDelivery: row.expected_delivery ?? null,
    deliveredAt: row.delivered_at ?? null,
    deliveryNotes: row.delivery_notes ?? null,
    createdAt: row.created_at,
  };
}

function fail(error: { message: string; code?: string }): never {
  // `not_found`, `already_delivered`, `empty_basket`, `invalid_item`,
  // `invalid_quantity`, `catalog_item_not_found`, `project_not_found`
  // viennent des deux RPC : leur texte est déjà précis, un message générique
  // ferait perdre la seule information utile à l'écran.
  throw new DomainError("conflict", `Opération refusée par la base : ${error.message}`, {
    pgCode: error.code,
  });
}

export function createSupabaseMaterialOrdersRepository(
  clientFor: DbClientFactory,
): MaterialOrdersRepository {
  return {
    async list(ctx: ExecutionContext, status?: MaterialOrderStatus): Promise<MaterialOrder[]> {
      let query = clientFor(ctx)
        .from("material_orders")
        .select("*")
        .order("created_at", { ascending: false });
      if (status) query = query.eq("status", status);

      const { data, error } = await query;
      if (error) fail(error);
      return (data ?? []).map(toOrder);
    },

    async createFromBasket(ctx, items, projectId, neededBy): Promise<MaterialOrder[]> {
      const { data, error } = await clientFor(ctx).rpc("create_material_orders_from_basket", {
        p_items: items.map((i) => ({ catalog_item_id: i.catalogItemId, quantity: i.quantity })),
        p_project_id: projectId,
        p_needed_by: neededBy,
      });
      if (error) fail(error);
      return (data ?? []).map(toOrder);
    },

    async receive(ctx, orderId): Promise<void> {
      const { error } = await clientFor(ctx).rpc("receive_material_order", {
        p_order_id: orderId,
      });
      if (error) fail(error);
    },
  };
}
